import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';

export interface MediaItem {
  type: 'image' | 'video';
  url: string;
  title?: string;
}

interface MediaViewerProps {
  isOpen: boolean;
  onClose: () => void;
  media: MediaItem[];
  initialIndex?: number;
}

type OpenMediaViewerProps = Omit<MediaViewerProps, 'isOpen'> & {
  initialIndex: number;
};

const MediaViewerContent: React.FC<OpenMediaViewerProps> = ({ onClose, media, initialIndex }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const isDraggingRef = useRef(false);
  const dragRef = useRef({ startX: 0, startY: 0, startPosX: 0, startPosY: 0 });
  const mediaAreaRef = useRef<HTMLDivElement>(null);

  const resetViewport = useCallback(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setIsDragging(false);
    isDraggingRef.current = false;
  }, []);

  const showPrevious = useCallback(() => {
    resetViewport();
    setCurrentIndex((previousIndex) => (previousIndex === 0 ? media.length - 1 : previousIndex - 1));
  }, [media.length, resetViewport]);

  const showNext = useCallback(() => {
    resetViewport();
    setCurrentIndex((previousIndex) => (previousIndex === media.length - 1 ? 0 : previousIndex + 1));
  }, [media.length, resetViewport]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          showPrevious();
          break;
        case 'ArrowRight':
          showNext();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, showNext, showPrevious]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    const el = mediaAreaRef.current;
    if (!el) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setScale(prev => Math.max(0.5, Math.min(5, prev + delta)));
    };

    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (scale <= 1) return;
    e.preventDefault();
    setIsDragging(true);
    isDraggingRef.current = true;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPosX: position.x,
      startPosY: position.y,
    };
  }, [scale, position]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDraggingRef.current) return;
    setPosition({
      x: dragRef.current.startPosX + (e.clientX - dragRef.current.startX),
      y: dragRef.current.startPosY + (e.clientY - dragRef.current.startY),
    });
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    isDraggingRef.current = false;
  }, []);

  const currentMedia = media[currentIndex];
  if (!currentMedia) return null;

  const portal = (
    <div
      className="fixed inset-0 z-[10000] bg-black/90 flex items-center justify-center animate-fade-in select-none"
      onClick={onClose}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onMouseMove={handleMouseMove}
    >
      {/* Close button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-4 right-4 text-white/80 hover:text-white z-10 p-2 hover:bg-white/10 rounded-full transition text-2xl w-10 h-10 flex items-center justify-center"
        aria-label="Close"
      >
        ✕
      </button>

      {/* Counter */}
      <div className="absolute top-4 left-4 text-white/80 text-sm font-medium z-10 bg-black/40 px-3 py-1.5 rounded-full pointer-events-none">
        {currentIndex + 1} / {media.length}
      </div>

      {/* Title */}
      {currentMedia.title && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-sm font-medium z-10 bg-black/40 px-4 py-2 rounded-full max-w-[80%] truncate pointer-events-none">
          {currentMedia.title}
        </div>
      )}

      {/* Navigation arrows */}
      {media.length > 1 && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              showPrevious();
            }}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white z-10 p-3 hover:bg-white/10 rounded-full transition text-3xl w-12 h-12 flex items-center justify-center"
            aria-label="Previous"
          >
            ◀
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              showNext();
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white z-10 p-3 hover:bg-white/10 rounded-full transition text-3xl w-12 h-12 flex items-center justify-center"
            aria-label="Next"
          >
            ▶
          </button>
        </>
      )}

      {/* Media area */}
      <div
        ref={mediaAreaRef}
        className="flex items-center justify-center w-full h-full p-4"
      >
        <div
          className="inline-flex w-auto h-auto max-w-[calc(100vw-2rem)] max-h-[90vh]"
          style={{
            transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
            cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
            transition: isDragging ? 'none' : 'transform 0.15s ease-out',
          }}
          onMouseDown={handleMouseDown}
          onClick={(e) => e.stopPropagation()}
        >
          {currentMedia.type === 'video' ? (
            <video
              src={currentMedia.url}
              controls
              autoPlay
              className="block max-w-full max-h-[90vh] object-contain rounded-lg"
              draggable={false}
            />
          ) : (
            <img
              src={currentMedia.url}
              alt={currentMedia.title || 'Media'}
              className="block max-w-full max-h-[90vh] object-contain"
              draggable={false}
            />
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(portal, document.body);
};

const MediaViewer: React.FC<MediaViewerProps> = ({ isOpen, onClose, media, initialIndex = 0 }) => {
  if (!isOpen || media.length === 0) return null;

  return <MediaViewerContent key={initialIndex} onClose={onClose} media={media} initialIndex={initialIndex} />;
};

export default MediaViewer;
